import * as React from "react";
import { styled, useTheme, alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import FolderIcon from "@mui/icons-material/Folder";
import MailIcon from "@mui/icons-material/Mail";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import Badge from "@mui/material/Badge";
import AccountCircle from "@mui/icons-material/AccountCircle";
import NotificationsIcon from "@mui/icons-material/Notifications";
import MoreIcon from "@mui/icons-material/MoreVert";
import "./SideBar.css";
import { useSpring, animated } from "@react-spring/web";
import HubSharpIcon from "@mui/icons-material/HubSharp";
import SettingsSharpIcon from "@mui/icons-material/SettingsSharp";
import PaletteSharpIcon from "@mui/icons-material/PaletteSharp";
import AlternateEmailSharpIcon from "@mui/icons-material/AlternateEmailSharp";
import Content from "../content/Content";
import AutoAwesomeMosaicIcon from "@mui/icons-material/AutoAwesomeMosaic";
import SourceIcon from "@mui/icons-material/Source";
import PublicIcon from "@mui/icons-material/Public";
import { CustomScroll } from "react-custom-scroll";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import LocalPoliceIcon from "@mui/icons-material/LocalPolice";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { API } from "../../assets/staticData";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import Uploader from "../uploader/Uploader";

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(3),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "25ch",
    },
  },
}));

export default function SideBar() {
  const [open, setOpen] = React.useState(true);
  const [uploader, setUploader] = React.useState(false);
  const [content, setConent] = React.useState("General");
  const [user, setUser] = React.useState();
  let userToken = localStorage.getItem("userToken");

  const handleDrawerOpen = () => {
    setOpen(!open);
  };

  const handleDrawerClose = () => {
    setOpen(!open);
  };

  React.useEffect(() => {
    async function fetchData() {
      try {
        const response = await fetch(`${API}/users/me?populate=*`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
          method: "GET",
        });
        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }
        const res = await response.json();
        setUser(res);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }

    fetchData();
  }, [content]);

  // Animation
  const scales = useSpring({
    transform: open ? "scale(1)" : "scale(0)",
    position: open ? "relative" : "absolute",
    // visibility: open ? "visible" : "hidden",
    config: { tension: 400, friction: 30 },
  });

  const menuId = "primary-search-account-menu";

  const hasReadPermission = (label) => {
    const permission = user?.perms?.find((perm) => perm.label === label);
    return permission ? permission.read : false;
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar style={{ backgroundColor: "#00166a" }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: "none" }),
            }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            Mishov Markets
          </Typography>

          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase placeholder="Search…" inputProps={{ "aria-label": "search" }} />
          </Search>
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex" }}>
            {/* <IconButton
              size="large"
              edge="end"
              color="inherit"
              onClick={() => setUploader(!uploader)}
            >
              <FileUploadIcon />
            {}
            </IconButton> */}
            {user?.id == 40 && (
              <IconButton
                size="large"
                edge="end"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={() => setConent("Admin")}
                color="inherit">
                <AdminPanelSettingsIcon />
              </IconButton>
            )}
            {hasReadPermission("Admin") && (
              <IconButton
                size="large"
                edge="end"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={() => setConent("Admin")}
                color="inherit">
                <AdminPanelSettingsIcon />
              </IconButton>
            )}
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={() => setConent("Account")}
              color="inherit">
              <AccountCircle />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer variant="permanent" open={open}>
        <DrawerHeader style={{ backgroundColor: "#00166a" }}>
          <IconButton style={{ color: "#fff" }} onClick={handleDrawerClose}>
            <MenuIcon />
          </IconButton>
        </DrawerHeader>

        {user && (
          <CustomScroll heightRelativeToParent="1003000%">
            <Divider />
            {hasReadPermission("Languages") && (
              <List>
                <animated.div
                  style={{
                    ...scales,
                  }}>
                  <Typography fontWeight={"600"} sx={{ pl: 2.5, pt: 2 }}>
                    Global
                  </Typography>
                </animated.div>
                {["Languages"].map((text, index) => (
                  <ListItem key={text} disablePadding sx={{ display: "block" }}>
                    <ListItemButton
                      onClick={() => setConent(text)}
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? "initial" : "center",
                        px: 2.5,
                      }}>
                      <ListItemIcon
                        style={{ color: "#00166a" }}
                        sx={{
                          minWidth: 0,
                          mr: open ? 3 : "auto",
                          justifyContent: "center",
                        }}>
                        {index == 0 && <PublicIcon />}
                      </ListItemIcon>
                      <ListItemText
                        primary={text}
                        sx={{ opacity: open ? 1 : 0 }}
                        style={{ color: "#777" }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
            <Divider />
            <Divider />
            <List>
              <animated.div
                style={{
                  ...scales,
                }}>
                <Typography fontWeight={"600"} sx={{ pl: 2.5, pt: 2 }}>
                  Site Settings
                </Typography>
              </animated.div>
              {["General", "SEO", "Menu", "Social Media", "Theme", "Policies"].map(
                (text, index) =>
                  hasReadPermission(text) && (
                    <ListItem key={text} disablePadding sx={{ display: "block" }}>
                      <ListItemButton
                        onClick={() => setConent(text)}
                        sx={{
                          minHeight: 48,
                          justifyContent: open ? "initial" : "center",
                          px: 2.5,
                        }}>
                        <ListItemIcon
                          style={{ color: "#00166a" }}
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : "auto",
                            justifyContent: "center",
                          }}>
                          {index == 0 && <SettingsSharpIcon />}
                          {index == 1 && <HubSharpIcon />}
                          {index == 2 && <MenuOpenIcon />}
                          {index == 3 && <AlternateEmailSharpIcon />}
                          {index == 4 && <PaletteSharpIcon />}
                          {index == 5 && <LocalPoliceIcon />}
                        </ListItemIcon>
                        <ListItemText
                          primary={text}
                          sx={{ opacity: open ? 1 : 0 }}
                          style={{ color: "#777" }}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
              )}
            </List>
            <Divider />
            {hasReadPermission("Home Page") && (
              <>
                <List>
                  <animated.div style={{ ...scales }}>
                    <Typography fontWeight={"600"} sx={{ pl: 2.5, pt: 2 }}>
                      Home Page
                    </Typography>
                  </animated.div>
                  {["Banner", "About Section", "Service Cards", "Account Types"].map(
                    (text, index) => (
                      <ListItem key={text} disablePadding sx={{ display: "block" }}>
                        <ListItemButton
                          onClick={() => setConent(text)}
                          sx={{
                            minHeight: 48,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                          }}>
                          <ListItemIcon
                            style={{ color: "#00166a" }}
                            sx={{
                              minWidth: 0,
                              mr: open ? 3 : "auto",
                              justifyContent: "center",
                            }}>
                            <AutoAwesomeMosaicIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={text}
                            sx={{ opacity: open ? 1 : 0 }}
                            style={{ color: "#777" }}
                          />
                        </ListItemButton>
                      </ListItem>
                    )
                  )}
                </List>
                <Divider />
              </>
            )}
            <List>
              <animated.div style={{ ...scales }}>
                <Typography fontWeight={"600"} sx={{ pl: 2.5, pt: 2 }}>
                  Pages
                </Typography>
              </animated.div>
              {[
                "About Us",
                "Platfroms",
                "Help Center",
                "Blog",
                "Account Types Full",
                "D&W Table",
                "D&W Body",
                "Instruments",
                "Promotions",
                "IB",
                "IB Box / FAQ / Calc",
                "Partnership",
                "Employment"
              ].map(
                (text, index) =>
                  hasReadPermission(text) && (
                    <ListItem key={text} disablePadding sx={{ display: "block" }}>
                      <ListItemButton
                        onClick={() => setConent(text)}
                        sx={{
                          minHeight: 48,
                          justifyContent: open ? "initial" : "center",
                          px: 2.5,
                        }}>
                        <ListItemIcon
                          style={{ color: "#00166a" }}
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : "auto",
                            justifyContent: "center",
                          }}>
                          <AutoAwesomeMosaicIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={text}
                          sx={{ opacity: open ? 1 : 0 }}
                          style={{ color: "#777" }}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
              )}
            </List>
            <Divider />
            {hasReadPermission("Calculation Pages") && (
              <>
                <List>
                  <animated.div style={{ ...scales }}>
                    <Typography fontWeight={"600"} sx={{ pl: 2.5, pt: 2 }}>
                      Caclculation Pages
                    </Typography>
                  </animated.div>
                  {["Profit & Loss", "Margin", "Spread"].map((text, index) => (
                    <ListItem key={text} disablePadding sx={{ display: "block" }}>
                      <ListItemButton
                        onClick={() => setConent(text)}
                        sx={{
                          minHeight: 48,
                          justifyContent: open ? "initial" : "center",
                          px: 2.5,
                        }}>
                        <ListItemIcon
                          style={{ color: "#00166a" }}
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : "auto",
                            justifyContent: "center",
                          }}>
                          <AutoAwesomeMosaicIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={text}
                          sx={{ opacity: open ? 1 : 0 }}
                          style={{ color: "#777" }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
                <Divider />
              </>
            )}

            <List>
              <animated.div style={{ ...scales }}>
                <Typography fontWeight={"600"} sx={{ pl: 2.5, pt: 2 }}>
                  Content
                </Typography>
              </animated.div>
              {["Articles", "Market Analysis", "Videos", "Categories", "Promos"].map(
                (text, index) =>
                  hasReadPermission(text) && (
                    <ListItem key={text} disablePadding sx={{ display: "block" }}>
                      <ListItemButton
                        onClick={() => setConent(text)}
                        sx={{
                          minHeight: 48,
                          justifyContent: open ? "initial" : "center",
                          px: 2.5,
                        }}>
                        <ListItemIcon
                          style={{ color: "#00166a" }}
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : "auto",
                            justifyContent: "center",
                          }}>
                          <SourceIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={text}
                          sx={{ opacity: open ? 1 : 0 }}
                          style={{ color: "#777" }}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
              )}
            </List>
            <Divider />
            <List>
              <animated.div style={{ ...scales }}>
                <Typography fontWeight={"600"} sx={{ pl: 2.5, pt: 2 }}>
                  Other
                </Typography>
              </animated.div>
              {[
                "Payment Methods",
                "History Slider",
                "Contact Us & FAQ",
                "Newsletter",
                "Instructor",
                'Appointment',
                'Announcement Messages',
                "Sitemap",
                "Footer",
                "Redirects",
              ].map(
                (text, index) =>
                  hasReadPermission(text) && (
                    <ListItem key={text} disablePadding sx={{ display: "block" }}>
                      <ListItemButton
                        onClick={() => setConent(text)}
                        sx={{
                          minHeight: 48,
                          justifyContent: open ? "initial" : "center",
                          px: 2.5,
                        }}>
                        <ListItemIcon
                          style={{ color: "#00166a" }}
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : "auto",
                            justifyContent: "center",
                          }}>
                          <FolderIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={text}
                          sx={{ opacity: open ? 1 : 0 }}
                          style={{ color: "#777" }}
                        />
                      </ListItemButton>
                    </ListItem>
                  )
              )}


              {/* Marketing Pages */}
              {hasReadPermission("Contest Marketing") && (
                <>
                  <List>
                    <animated.div style={{ ...scales }}>
                      <Typography fontWeight={"600"} sx={{ pl: 2.5, pt: 2 }}>
                        Marketing Pages
                      </Typography>
                    </animated.div>
                    {["Contest"].map((text, index) => (
                      <ListItem key={text} disablePadding sx={{ display: "block" }}>
                        <ListItemButton
                          onClick={() => setConent(text)}
                          sx={{
                            minHeight: 48,
                            justifyContent: open ? "initial" : "center",
                            px: 2.5,
                          }}>
                          <ListItemIcon
                            style={{ color: "#00166a" }}
                            sx={{
                              minWidth: 0,
                              mr: open ? 3 : "auto",
                              justifyContent: "center",
                            }}>
                            <AutoAwesomeMosaicIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={text}
                            sx={{ opacity: open ? 1 : 0 }}
                            style={{ color: "#777" }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                  <Divider />
                </>
              )}
            </List>
          </CustomScroll>
        )}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
        }}>
        <DrawerHeader />
        <div
          style={{
            margin: 10,
            borderRadius: 10,
            padding: 30,
            backgroundColor: "#fff",
            boxShadow: "0 0 5px #00000030",
            maxWidth: 1100,
          }}>
          {user && <Content content={content} user={user} />}
        </div>
      </Box>

      <Uploader uploader={uploader} setUploader={setUploader} />
    </Box>
  );
}
